/** @jsxImportSource @emotion/react */
import { useCallback, useContext, useEffect, useMemo, useReducer, useState, memo } from 'react';
import { css } from '@emotion/react';
import styled from '@emotion/styled';
import Skeleton from '@mui/material/Skeleton';
import type { Chat } from '@trinity/chat';
import type { HcpSearchResult } from '@trinity/chat';
import type { KolSearchForm, KolSearchResult } from '@trinity/chat';
import { ExternalLink } from 'react-feather';
import { AnalysisQueryHint, DownloadQueryHint } from '@trinity/chat-components/components/presentation/Message.QueryHint/index.js';
import { Tooltip } from '@trinity/chat-components/components/presentation/Tooltip';
import { useActiveBrandIdentifier } from '@trinity/chat-components/containers/UserBrands/useActiveBrandIdentifier';
import { useTablePaginationHelpers } from '@trinity/chat-components/hooks';
import { Checkbox } from '../presentation/Checkbox';
import { TablePagination } from '../presentation/TablePagination';
import { ActiveChatSessionContext, KolSearchResultsContext, KolSearchResultsSelectionContext, SmartRoutingHcpContext } from './context';
import { useDownloadHcpResults } from './hooks/useDownloadHcpResults';
import { KolResultsContainer, KolResultsTable } from './KolSearch.Table';
import { KolTableContainer } from './KolTableContainer';
import { BasicSystemUserMessage } from './Message.System.Basic';
import { usePlaceholderStyles } from './Message.System.styles';
import type { SmartRoutingAction } from './SmartRouting.Container';

type Props = {
  state: Chat.QueryState.SmartRouting;
  dispatch: (action: SmartRoutingAction) => void;
  queryIdentifier?: string;
  onRouteSubmitted?: (selectedHcps: number[]) => Promise<void>;
};

function buildInitialState(): State {
  return {
    form: {
      kolId: null,
      name: '',
      location: null,
    },
    results: [],
    status: 'form-entry',
  };
}

export const SmartRoutingHcpSelection = (props: Props) => {
  return (
    <KolTableContainer>
      <SmartRoutingHcpSelectionContent {...props} />
    </KolTableContainer>
  );
};

const SmartRoutingHcpSelectionContent = (props: Props) => {
  const { dispatch: smartRoutingDispatch } = props;
  const [state, dispatch] = useReducer(reducer, buildInitialState());
  const chat = useContext(ActiveChatSessionContext);
  const kolSelection = useContext(KolSearchResultsSelectionContext);
  const placeholderStyles = usePlaceholderStyles();
  const { searchResults: contextResults, isSearchLoading, hasSearchCompleted } = useContext(SmartRoutingHcpContext);
  const brandIdentifier = useActiveBrandIdentifier();

  useEffect(() => {
    if (hasSearchCompleted) {
      dispatch({
        type: 'search-results-available',
        payload: {
          results: contextResults,
        },
      });
    } else if (isSearchLoading && state.status === 'form-entry') {
      dispatch({
        type: 'search-submitted',
      });
    }
  }, [contextResults, isSearchLoading, hasSearchCompleted, state.status]);

  const handleHcpSelected = useCallback((item: KolSearchResult) => {
    // For individual HCP selection (if needed)
    dispatch({
      type: 'hcp-selected',
    });
  }, []);

  const { download: downloadHcpResults } = useDownloadHcpResults({
    brandIdentifier,
    chatInstanceId: chat.id,
    queryIdentifier: props.queryIdentifier,
  });

  const showResults = useMemo(() => {
    return ['form-results'].includes(state.status);
  }, [state.status]);

  const showLoading = useMemo(() => {
    return ['form-submitted'].includes(state.status);
  }, [state.status]);

  // Determine result type - will always be all KOL or all HCP, never mixed
  const resultType = useMemo(() => {
    return props.state.searchIndex ? 'hcp' : 'kol';
  }, [props.state.searchIndex]);

  const handleGenerateRoute = useCallback(() => {
    const selectedIds = kolSelection.selected;
    if (selectedIds.length > 0) {
      smartRoutingDispatch({
        type: 'submit-route',
        payload: { selectedHcps: selectedIds },
      });
      props.onRouteSubmitted?.(selectedIds);
    }
  }, [kolSelection.selected, smartRoutingDispatch, props]);

  const generateRouteDisabled = useMemo(() => {
    return kolSelection.selected.length < 2;
  }, [kolSelection.selected.length]);

  const kolResults = useMemo(() => {
    return resultType === 'kol' ? (state.results as KolSearchResult[]) : [];
  }, [state.results, resultType]);

  const hcpResults = useMemo(() => {
    return resultType === 'hcp' ? (state.results as HcpSearchResult[]) : [];
  }, [state.results, resultType]);

  const systemCss = useMemo(() => {
    if (state.status !== 'form-results') return undefined;

    return css({
      maxWidth: '100%',
    });
  }, [state.status]);

  const message = useMemo(() => {
    if (state.results.length > 0) {
      if (props.state.startingLocation?.type === 'hcp' &&
        props.state.startingLocation?.name) {
        return `Here are the HCPs nearby ${props.state.startingLocation.name}`;
      }
      return `Here are the HCPs near your selected location:`;
    }
    return `No HCPs found near your selected location.`;
  }, [state.results.length, props.state.startingLocation]);

  return (
    <KolSearchResultsContext.Provider value={kolResults}>
      <BasicSystemUserMessage bodyStyles={systemCss}>
        {showResults && <Message>{message}</Message>}
        {showLoading && (
          <div css={placeholderStyles.skeleton}>
            <Skeleton
              css={placeholderStyles.skeletonItem}
              variant="text"
              width="95%" />
            <Skeleton
              css={placeholderStyles.skeletonItem}
              variant="text"
              width="95%" />
            <Skeleton
              css={placeholderStyles.skeletonItem}
              variant="text"
              width="60%" />
          </div>
        )}
        {showResults && resultType === 'kol' && (
          <ResultsWrapper>
            <KolResultsContainer records={kolResults}>
              <StyledResultsTable
                isInteractive={true}
                onSelectProfile={handleHcpSelected}
                smartRoutingDisabled={false}
              />
            </KolResultsContainer>
          </ResultsWrapper>
        )}
        {showResults && (resultType === 'hcp') && (
          <ResultsWrapper>
            <HcpResultsTable results={hcpResults} />
          </ResultsWrapper>
        )}
        {showResults && state.results.length > 0 && (
          <>
            <StyledDownloadButton value="Download List" onClick={downloadHcpResults} />
            <Tooltip
              title={generateRouteDisabled ? 'Please select at least 2 HCPs to generate a route.' : ''}
              placement="top">
              <HintWrapper>
                <AnalysisQueryHint
                  disabled={generateRouteDisabled}
                  value={'Generate Route'}
                  onClick={handleGenerateRoute}
                />
              </HintWrapper>
            </Tooltip>
          </>
        )}
      </BasicSystemUserMessage>
    </KolSearchResultsContext.Provider>
  );
};

const Message = styled.div({
  marginBottom: 15,
});

const StyledDownloadButton = styled(DownloadQueryHint)({
  marginTop: 15,
});

const HintWrapper = styled.div({
  marginTop: 15,
  display: 'inline-flex',
});

const ResultsWrapper = styled.div({
  marginTop: 15,
});

const StyledResultsTable = styled(KolResultsTable)({
  marginTop: 10,
});

const HcpResultsTable = ({ results }: { results: HcpSearchResult[] }) => {
  const kolSelection = useContext(KolSearchResultsSelectionContext);

  const [pageIndex, setPageIndex] = useState(0);
  const pageSize = 5;

  const handleHcpToggle = (hcp: HcpSearchResult) => {
    kolSelection.toggle(+hcp.hcpIdentifier);
  };

  const columnWidths = useMemo(() => [60, 150, 190, 190, 175, 150].filter(Boolean), []);

  const total = results.length;
  const paginatedResults = useMemo(() => {
    const startIndex = pageIndex * pageSize;
    return results.slice(startIndex, startIndex + pageSize);
  }, [results, pageIndex, pageSize]);

  const hasResults = results.length > 0;

  const buildMailTo = (email: string) => `mailto:${email}`;

  return (
    <HcpContainer>
      <HcpHorizontalScrollContainer>
        <HcpInnerContainer>
          <HcpHeaderContainer columns={columnWidths}>
            <HcpCheckboxAttributeContainer>
              <HcpAttribute>Select</HcpAttribute>
            </HcpCheckboxAttributeContainer>
            <HcpNameAttributeContainer>
              <HcpAttribute>Name</HcpAttribute>
            </HcpNameAttributeContainer>
            <HcpSpecialtyAttributeContainer>
              <HcpAttribute>Specialty</HcpAttribute>
            </HcpSpecialtyAttributeContainer>
            <HcpLocationAttributeContainer>
              <HcpAttribute>Location</HcpAttribute>
            </HcpLocationAttributeContainer>
            <HcpEmailAttributeContainer>
              <HcpAttribute>Email</HcpAttribute>
            </HcpEmailAttributeContainer>
            <HcpPhoneAttributeContainer>
              <HcpAttribute>Distance</HcpAttribute>
            </HcpPhoneAttributeContainer>
          </HcpHeaderContainer>

          {hasResults ? (
            paginatedResults.map((hcp, index) => {
              const isSelected = kolSelection.selected.includes(+hcp.hcpIdentifier);
              return (
                <HcpFlexibleItemContainer key={hcp.hcpIdentifier || index} columns={columnWidths}>
                  <HcpCheckboxAttributeContainer>
                    <HcpSelectAttribute>
                      <Checkbox
                        checked={isSelected}
                        onChange={() => handleHcpToggle(hcp)}
                      />
                    </HcpSelectAttribute>
                  </HcpCheckboxAttributeContainer>
                  <HcpNameAttributeContainer>
                    <HcpNameAttribute>{hcp.name.replace(/\b([A-Z])\b/g, '$1.')}</HcpNameAttribute>
                  </HcpNameAttributeContainer>
                  <HcpSpecialtyAttributeContainer>
                    <HcpSpecialtyAttribute>
                      {hcp.specialty ? (
                        <Tooltip
                          title={hcp.specialty}
                          enterDelay={1000}
                          enterNextDelay={500}>
                          <div css={{ overflow: 'hidden' }}>{hcp.specialty.toLowerCase().replace(/\b\w/g, l => l.toUpperCase())}</div>
                        </Tooltip>
                      ) : (
                        <HcpPlaceholderText>None</HcpPlaceholderText>
                      )}
                    </HcpSpecialtyAttribute>
                  </HcpSpecialtyAttributeContainer>
                  <HcpLocationAttributeContainer>
                    <HcpLocationAttribute>
                      {hcp.location ? hcp.location.toLowerCase().replace(/\b\w/g, l => l.toUpperCase()) : <HcpPlaceholderText>N/A</HcpPlaceholderText>}
                    </HcpLocationAttribute>
                  </HcpLocationAttributeContainer>
                  <HcpEmailAttributeContainer>
                    <HcpEmailAttribute>
                      {hcp.email ? (
                        <HcpEmailMailTo
                          href={buildMailTo(hcp.email)}
                          target="_blank"
                          rel="noreferrer">
                          <HcpEmailLinkText>{hcp.email}</HcpEmailLinkText>
                          <HcpStyledLinkIcon size={14} />
                        </HcpEmailMailTo>
                      ) : (
                        <HcpPlaceholderText>N/A</HcpPlaceholderText>
                      )}
                    </HcpEmailAttribute>
                  </HcpEmailAttributeContainer>
                  <HcpPhoneAttributeContainer>
                    <HcpPhoneAttribute>
                      {hcp.distance !== undefined ? `${hcp.distance.toFixed(2)} mi` : 'N/A'}
                    </HcpPhoneAttribute>
                  </HcpPhoneAttributeContainer>
                </HcpFlexibleItemContainer>
              );
            })
          ) : (
            <HcpFlexibleItemContainer columns={columnWidths}>
              <HcpAttributeContainer style={{ gridColumn: '1 / -1', textAlign: 'center', padding: '20px' }}>
                <HcpPlaceholderText>No HCPs found in this area. Try expanding your search radius or selecting a different location.</HcpPlaceholderText>
              </HcpAttributeContainer>
            </HcpFlexibleItemContainer>
          )}
        </HcpInnerContainer>
      </HcpHorizontalScrollContainer>
      <HcpPaginationFooter
        pageIndex={pageIndex}
        pageSize={pageSize}
        setPageIndex={setPageIndex}
        total={total} />
    </HcpContainer>
  );
};

const HcpContainer = styled.div(({ theme }) => ({
  boxSizing: 'border-box',
  border: `2px solid ${theme.palette.gray.light1}`,
  borderRadius: 4,
  margin: 0,
  padding: 0,
  marginTop: 10,
}));

const HcpHorizontalScrollContainer = styled.div({
  overflowX: 'auto',
  display: 'flex',
  flexDirection: 'column',
  height: '100%',
});

const HcpInnerContainer = styled.div({
  display: 'flex',
  flexDirection: 'column',
  width: 'fit-content',
  backgroundColor: 'white',
});

type HcpColumnsProps = { columns: number[] };
const HcpFlexibleItemContainer = styled.div<HcpColumnsProps>(({ columns }) => ({
  boxSizing: 'border-box',
  display: 'grid',
  gridTemplateColumns: columns.map(c => `${c}px`).join(' '),
  width: '100%',
}));

const HcpHeaderContainer = styled(HcpFlexibleItemContainer)(({ columns, theme }) => ({
  boxSizing: 'border-box',
  display: 'grid',
  gridTemplateColumns: columns.map(c => `${c}px`).join(' '),
  flexShrink: 0,

  [`& > *`]: {
    backgroundColor: theme.palette.gray.light2,
    fontFamily: theme.fonts.semiBold,
    fontSize: 14,
  },
}));

const HcpAttributeContainer = styled.div({
  boxSizing: 'border-box',
  display: 'grid',
  gridTemplateColumns: 'repeat(auto-fit, minmax(var(--column-width-min), 1fr))',
});

const HcpAttribute = styled.div(({ theme }) => ({
  boxSizing: 'border-box',
  borderRight: `1px solid ${theme.palette.gray.light2}`,
  borderBottom: `1px solid ${theme.palette.gray.light2}`,
  padding: 8,
  fontSize: 14,
  overflow: 'hidden',
  height: '100%',
}));

const HcpCheckboxAttributeContainer = styled(HcpAttributeContainer)({
  ['--column-width-min']: 60,
});

const HcpNameAttributeContainer = styled(HcpAttributeContainer)({
  ['--column-width-min']: 150,
});

const HcpSpecialtyAttributeContainer = styled(HcpAttributeContainer)({
  ['--column-width-min']: 190,
});

const HcpLocationAttributeContainer = styled(HcpAttributeContainer)({
  ['--column-width-min']: 190,
});

const HcpEmailAttributeContainer = styled(HcpAttributeContainer)({
  ['--column-width-min']: 175,
});

const HcpPhoneAttributeContainer = styled(HcpAttributeContainer)({
  ['--column-width-min']: 150,
});

const HcpSelectAttribute = styled(HcpAttribute)({
  display: 'flex',
  justifyContent: 'center',
});

const HcpNameAttribute = styled(HcpAttribute)({});

const HcpSpecialtyAttribute = styled(HcpAttribute)({
  display: 'flex',
  overflow: 'hidden',
});

const HcpLocationAttribute = styled(HcpAttribute)({
  display: 'flex',
  alignItems: 'center',
  transition: 'all 150ms',
});

const HcpEmailAttribute = styled(HcpAttribute)({
  display: 'flex',
  alignItems: 'center',
  transition: 'all 150ms',
});

const HcpPhoneAttribute = styled(HcpAttribute)({
  display: 'flex',
  alignItems: 'center',
  transition: 'all 150ms',
});

const HcpStyledLinkIcon = styled(ExternalLink)(({ theme }) => ({
  color: theme.palette.hyperlink,
  transition: 'all 150ms',
  flexShrink: 0,
  marginLeft: 4,
}));

const HcpEmailMailTo = styled.a(({ theme }) => ({
  display: 'flex',
  alignItems: 'center',
  transition: 'all 150ms',
  overflow: 'hidden',
  height: '100%',

  ':hover': {
    color: theme.palette.hyperlink,
  },

  [`> ${String(HcpStyledLinkIcon)}`]: {
    opacity: 0,
  },

  [`:hover > ${String(HcpStyledLinkIcon)}`]: {
    opacity: 1,
  },
}));

const HcpEmailLinkText = styled.div({
  height: '100%',
  wordBreak: 'break-all',
});

const HcpPlaceholderText = styled.div(({ theme }) => ({
  color: theme.palette.gray.main,
}));

// HCP Pagination Footer Component
type HcpPaginationFooterProps = {
  pageIndex: number;
  pageSize: number;
  setPageIndex: (pageIndex: number) => void;
  total: number;
};

const HcpPaginationFooter = memo(({ pageIndex, pageSize, setPageIndex, total }: HcpPaginationFooterProps) => {
  const {
    nextPage,
    previousPage,
    canNextPage,
    canPreviousPage,
  } = useTablePaginationHelpers({
    pageIndex,
    pageSize,
    setPageIndex,
    total,
  });

  if (total <= pageSize) {
    return null;
  }

  return (
    <HcpFooter>
      <TablePagination
        canNextPage={canNextPage}
        canPreviousPage={canPreviousPage}
        nextPage={nextPage}
        previousPage={previousPage}
        pageIndex={pageIndex}
        pageSize={pageSize}
        totalCount={total} />
    </HcpFooter>
  );
});

HcpPaginationFooter.displayName = 'HcpPaginationFooter';

const HcpFooter = styled.div({
  display: 'flex',
  justifyContent: 'flex-end',
  alignItems: 'center',
  paddingRight: 10,
  height: 34,
  boxSizing: 'border-box',
});



const reducer = (state: State, action: Action): State => {
  switch (action.type) {
    case 'name-changed':
      return {
        ...state,
        form: {
          ...state.form,
          name: action.payload,
        },
      };
    case 'form-updated':
      return {
        ...state,
        form: action.payload,
      };
    case 'results-changed':
      return {
        ...state,
        results: action.payload,
      };
    case 'form-reset':
      return {
        form: {
          kolId: null,
          name: '',
          location: null,
        },
        results: [],
        status: 'form-entry',
      };
    case 'search-errored':
      return {
        ...state,
        status: 'form-error',
      };
    case 'search-submitted':
      return {
        ...state,
        status: 'form-submitted',
      };
    case 'hcp-selected': {
      return {
        ...state,
        status: 'hcp-selected',
      };
    };
    case 'search-cancelled': {
      return {
        ...state,
        status: 'form-cancelled',
      };
    }
    case 'search-results-available': {
      return {
        ...state,
        results: action.payload.results,
        status: 'form-results',
      };
    }
    default:
      return state;
  }
};

type NameChangedAction = {
  type: 'name-changed';
  payload: string;
};
type ResultsChangedAction = {
  type: 'results-changed';
  payload: Array<KolSearchResult | HcpSearchResult>;
};
type FormUpdatedAction = {
  type: 'form-updated';
  payload: KolSearchForm;
};
type FormReset = {
  type: 'form-reset';
};
type SearchErrored = {
  type: 'search-errored';
};
type SearchSubmitted = {
  type: 'search-submitted';
};
type ResultsAvailable = {
  type: 'search-results-available';
  payload: {
    results: Array<KolSearchResult | HcpSearchResult>;
  };
};
type HcpSelected = {
  type: 'hcp-selected';
};
type SearchCancelled = {
  type: 'search-cancelled';
};
type Action =
  NameChangedAction |
  ResultsChangedAction |
  FormUpdatedAction |
  FormReset |
  SearchErrored |
  SearchSubmitted |
  ResultsAvailable |
  HcpSelected |
  SearchCancelled;

type State = {
  form: KolSearchForm;
  results: Array<KolSearchResult | HcpSearchResult>;
  status: Chat.QueryState.HcpSelection['status'];
};
